import React, { useEffect, useState } from 'react';
import { Toolbar, CardContent, Button, Typography, TextField, FormControl, InputLabel, Select, MenuItem } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import { useAlerts } from 'hooks/alerts-hooks';
import BasicField from '../../basic/BasicField';
import CollectionItemHeader from '../../layout/CollectionItemHeader';
import AppLoader from '../../basic/AppLoader';
import { useItem } from '../../../hooks/atoms/item-hook';
import CollectionKeys from '../../../hooks/molecules/collection-keys';

function isJSON(str) {
  try {
    return (JSON.parse(str) && !!str);
  } catch (e) {
    return false;
  }
}

const QuestionnaireJSON = ({ questions, setQuestions, updateQuestionnaireJSON, type }) => {
  const [ error, setError ] = useState(false);
  const [ loading, setLoading ] = useState(false);
  if ('null' === questions) {
    return (
      <Card style={ { width: '60vw' } }>
        <CardContent>
          <Typography variant='h6'>
            {`There is no questionnaire for ${ type }`}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <div>
      <TextField
        error={ error }
        style={ { width: '60vw' } }
        label='Edit Questionnaire'
        variant='outlined'
        multiline
        onChange={ (e) => {
          setQuestions(e.target.value);
          setError(!isJSON(e.target.value));
        } }
        color={ error ? '' : 'secondary' }
        value={ questions }
      />
      <Button
        style={ { position: 'sticky', top: '100px', marginLeft: '30px' } }
        variant='contained'
        color='secondary'
        disabled={ error }
        onClick={ () => {
          setLoading(true);
          updateQuestionnaireJSON().finally(() => {
            setLoading(false);
          });
        } }
      >
        {!loading ? 'Update Questionnaire' : 'Loading...'}
      </Button>
    </div>
  );
};

const QuestionField = ({ type, value }) => {
  return (
    <Typography variant='subtitle1'>
      {type}
      :
      {' '}
      {value}
    </Typography>
  );
};

const QuestionList = ({ type, value }) => {
  return (
    <Typography variant='subtitle1'>
      {type}
      :
      <ul style={ { marginTop: '0' } }>
        {value.map((v) => {
          return (
            <li key={ v }>
              {v}
            </li>
          );
        })}
      </ul>
    </Typography>
  );
};

const Question = ({ question }) => {
  return (
    <Grid
      container
      style={ {
        boxShadow: 'rgba(150, 50, 93, 0.25) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px',
        padding: '15px',
      } }
    >
      <Grid item xs={ 12 }>
        <QuestionField type='ID' value={ question.id } />
      </Grid>
      {null != question.description_en && (
        <Grid item xs={ 12 }>
          <QuestionField type='Description' value={ question.description_en } />
        </Grid>
      )}
      {null != question.description_1_en && (
        <Grid item xs={ 12 }>
          <QuestionField type='Description 1' value={ question.description_1_en } />
        </Grid>
      )}
      {null != question.description_2_en && (
        <Grid item xs={ 12 }>
          <QuestionField type='Description 2' value={ question.description_2_en } />
        </Grid>
      )}
      {null != question.info_en && (
        <Grid item xs={ 12 }>
          <QuestionField type='Info' value={ question.info_en } />
        </Grid>
      )}
      <Grid item xs={ 12 }>
        <QuestionField type='Required' value={ String(question.required) } />
      </Grid>
      {
        'limit_up' in question && (
          <Grid item xs={ 12 }>
            <QuestionField type='Limit up' value={ question.limit_up } />
          </Grid>
        )
      }
      {
        'limit_down' in question && (
          <Grid item xs={ 12 }>
            <QuestionField type='Limit down' value={ question.limit_down } />
          </Grid>
        )
      }
      <Grid item xs={ 12 }>
        <QuestionField type='Type' value={ question.type } />
      </Grid>
      {
        ('one_choice' === question.type || 'multiple_choice' === question.type) && (
          <Grid item xs={ 12 }>
            <QuestionList type='Options' value={ question.options_en } />
          </Grid>
        )
      }
      {
        null != question.referrence && (
          <Grid item xs={ 12 }>
            <Typography variant='subtitle1'>
              Referrence:
            </Typography>
            <div style={ { marginLeft: '25px' } }>
              {
                !Array.isArray(question.referrence) && (
                  <div>
                    <QuestionField type='ID' value={ question.referrence.id } />
                    <QuestionList type='Options' value={ question.referrence.options_en } />
                  </div>
                )
              }
              {
                Array.isArray(question.referrence) && (
                  question.referrence.map((refer) => {
                    return (
                      <div key={ refer.id }>
                        <QuestionField type='ID' value={ refer.id } />
                        <QuestionList type='Options' value={ refer.options_en } />
                      </div>
                    );
                  })
                )
              }
            </div>
          </Grid>
        )
      }
      {
        null != question.referral && (
          <Grid item xs={ 12 }>
            <QuestionField type='Referral ID' value={ question.referral } />
          </Grid>
        )
      }
      {
        null != question.times && (
          <Grid item xs={ 12 }>
            <QuestionField type='Times' value={ question.times } />
          </Grid>
        )
      }
    </Grid>
  );
};

const QuestionnaireDetails = ({ id: theId }) => {
  const {
    item: questionnaire,
    itemIsLoading,
    itemError,
    updateItem,
  } = useItem(CollectionKeys.QUESTIONNAIRES, theId);

  const { alertSuccess } = useAlerts();

  const [ name, setName ] = useState('');
  const [ description, setDescription ] = useState('');
  const [ formID, setFormID ] = useState('');

  const [ type, setType ] = useState('patient');

  const [ questions, setQuestions ] = useState('');

  useEffect(() => {
    if (false === itemIsLoading && null != questionnaire) {
      setName(questionnaire.name);
      setDescription(questionnaire.description);
      setFormID(questionnaire.form_id);

      if ('doctor' === type) {
        setQuestions(JSON.stringify(questionnaire.questions_doctor, null, '\t'));
      } else if ('patient' === type) {
        setQuestions(JSON.stringify(questionnaire.questions, null, '\t'));
      } else if ('child' === type) {
        setQuestions(JSON.stringify(questionnaire.questions_child, null, '\t'));
      }
    }
  }, [ questionnaire, itemIsLoading, type ]);

  const updateQuestionnaireJSON = () => {
    if ('patient' === type) {
      return updateItem({ questions: JSON.parse(questions) }).then(() => {
        alertSuccess('Questionnaire was successfully updated');
      });
    }

    if ('doctor' === type) {
      return updateItem({ questions_doctor: JSON.parse(questions) }).then(() => {
        alertSuccess('Questionnaire was successfully updated');
      });
    }

    return updateItem({ questions_child: JSON.parse(questions) }).then(() => {
      alertSuccess('Questionnaire was successfully updated');
    });
  };

  const updateQuestionnaire = () => {
    return updateItem({ name, description, form_id: formID }).then(() => {
      alertSuccess('Questionnaire was successfully updated');
    });
  };

  if (itemIsLoading) {
    return <AppLoader />;
  }

  if (itemError) {
    return <p>Error</p>;
  }

  return (

    <>
      <CollectionItemHeader
        subtitle={ `Created at: ${ questionnaire?.created_at }` }
      />
      <Toolbar />
      <Grid container spacing={ 3 }>
        <Grid container item xs={ 12 } spacing={ 3 }>
          <Grid item xs={ 4 }>
            <Card style={ { width: '60vw' } }>
              <CardContent>
                <Grid item xs={ 12 }>
                  <BasicField
                    title='ID'
                    disabled
                    field={ questionnaire.id }
                  />
                  <BasicField
                    title='Name'
                    field={ name }
                    onChange={ (v) => { return setName(v); } }
                  />
                  <BasicField
                    title='Description'
                    field={ description }
                    onChange={ (v) => { return setDescription(v); } }
                  />
                </Grid>
                <Grid item xs={ 12 } style={ { marginTop: '10px' } }>
                  <Button variant='contained' color='primary' onClick={ updateQuestionnaire }>
                    Update
                  </Button>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Grid item>
          <Card style={ { width: '60vw' } }>
            <CardContent>
              <div style={ { display: 'flex', justifyContent: 'center' } }>
                <FormControl style={ { minWidth: '100px' } }>
                  <InputLabel id='demo-simple-select-label-region'>Type</InputLabel>
                  <Select
                    labelId='demo-simple-select-label-region'
                    id='demo-simple-select-region'
                    value={ type }
                    label='Type'
                    onChange={ (e) => {
                      setType(e.target.value);
                    } }
                  >
                    <MenuItem value='patient'>Patient</MenuItem>
                    <MenuItem value='doctor'>Doctor</MenuItem>
                    <MenuItem value='child'>Child</MenuItem>
                  </Select>
                </FormControl>
              </div>
              <Grid
                container
                spacing={ 3 }
                style={ { padding: '7px' } }
              >
                {
                  'doctor' === type && null !== questionnaire?.questions_doctor

                    && questionnaire?.questions_doctor.map((q) => {
                      return (
                        <Grid
                          item
                          key={ q.id }
                          xs={ 12 }
                        >
                          <Question question={ q } />
                        </Grid>
                      );
                    })

                }
                {
                  'child' === type && null !== questionnaire?.questions_child

                    && questionnaire?.questions_child.map((q) => {
                      return (
                        <Grid
                          item
                          key={ q.id }
                          xs={ 12 }
                        >
                          <Question question={ q } />
                        </Grid>
                      );
                    })

                }
                {
                  'patient' === type
                    && questionnaire.questions.map((q) => {
                      return (
                        <Grid
                          item
                          key={ q.id }
                          xs={ 12 }
                        >
                          <Question question={ q } />
                        </Grid>
                      );
                    })
                }

              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item>
          <QuestionnaireJSON
            questions={ questions }
            setQuestions={ setQuestions }
            updateQuestionnaireJSON={ updateQuestionnaireJSON }
            type={ type }
          />
        </Grid>
      </Grid>
    </>
  );
};

export default QuestionnaireDetails;
